import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { 
  Container, 
  Grid,
  useTheme,
  CircularProgress,
  Box,
  Backdrop
} from '@mui/material';
import {
  ListAlt as EntryStatusIcon,
  People as CompanyUsersIcon
} from '@mui/icons-material';

// 共通コンポーネントのインポート
import { PageHeader, ActionCard, ActiveSeasonStatus } from '../components/common';
import { protectedResources, scopes } from "../authConfig";
import useFetchWithMsal from '../hooks/useFetchWithMsal';

const HRManagerMenu = () => {    const navigate = useNavigate();
    const { instance } = useMsal();
    const theme = useTheme();
    const [userName, setUserName] = useState('');
    const [companyName, setCompanyName] = useState('');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [apiLoading, setApiLoading] = useState(false); // APIローディング状態を追加
    
    const { execute } = useFetchWithMsal({
        scopes: scopes.callapi
    });    // 会社情報を取得する関数
    const fetchCompanyData = React.useCallback(async (id) => {
        if (!id) return;
        
        try {
            // getMasterData APIを使用して会社情報を取得
            const response = await execute(
                'GET',
                `${protectedResources.getMasterData.endpoint}/company?id=${id}&resolveRelations=true`
            );
            
            if (response) {
                // 会社名を設定
                setCompanyName(response.companyName || '');
            } else {
                console.warn('会社データが見つかりませんでした');
            }
        } catch (error) {
            console.error('会社データ取得エラー:', error);
            setError('会社情報の取得に失敗しました');
        }
    }, [execute]);

    // 初期処理時に実行
    useEffect(() => {
        // アクティブなアカウント情報を取得
        if (!instance) {
            setError('認証インスタンスが初期化されていません');
            setLoading(false);
            return;
        }

        const activeAccount = instance.getActiveAccount();
        if (!activeAccount) {
            navigate('/');
            return;
        }

        try {
            // 権限チェック (人事向けページなので権限3のユーザーのみアクセス可能)
            if (activeAccount.idTokenClaims?.extension_PermissionType !== 3) {
                navigate('/error');
                return;
            }
            
            // ユーザー名を設定
            setUserName(activeAccount.name || activeAccount.username || '人事担当者');
              // ユーザーの会社IDを取得
            const userCompanyId = activeAccount.idTokenClaims?.extension_CompanyId;
            if (userCompanyId) {
                // APIを使用して会社情報を取得
                fetchCompanyData(userCompanyId)
                    .finally(() => setLoading(false));
            } else {
                console.warn('会社IDが見つかりません');
                setLoading(false);
            }
        } catch (error) {
            console.error('ユーザー情報取得エラー:', error);
            setError('ユーザー情報の取得に失敗しました');
            setLoading(false);
        }
    }, [instance, navigate, execute, fetchCompanyData]);

    // メニュー項目の定義
    const menuItems = [
        {
            icon: <EntryStatusIcon />,
            title: 'エントリー状況一覧',
            description: '応募者のエントリー状況を確認・管理できます',
            onClick: () => navigate('/entry-status'),
            color: theme.palette.primary.main
        },
        {
            icon: <CompanyUsersIcon />,
            title: '自社ユーザー一覧',
            description: '自社のユーザー情報を確認・管理できます',
            onClick: () => navigate('/company-users'),
            color: theme.palette.secondary.main
        }
    ];

    // ローディング表示
    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Container maxWidth="lg" sx={{ pt: 4, pb: 8 }}>
            <PageHeader 
              title="人事担当者ダッシュボード" 
              subtitle={`ようこそ、${userName}さん${companyName ? `（${companyName}）` : ''}`} 
            />
            
            {error && (
                <Box sx={{ mb: 2, p: 2, bgcolor: 'error.light', color: 'error.contrastText', borderRadius: 1 }}>
                    {error}
                </Box>            )}

            {/* エントリー受付中のシーズン情報を表示 */}
            <ActiveSeasonStatus onLoadingChange={setApiLoading} />

            <Grid container spacing={3}>
                {menuItems.map((item, index) => (
                    <Grid item xs={12} sm={6} md={4} key={index}>
                        <ActionCard {...item} />
                    </Grid>
                ))}
            </Grid>

            {/* API呼び出し中のシェード */}
            <Backdrop
                sx={{ 
                    color: '#fff', 
                    zIndex: (theme) => theme.zIndex.drawer + 1,
                    backdropFilter: 'blur(3px)',
                    backgroundColor: 'rgba(0, 0, 0, 0.5)' 
                }}
                open={apiLoading}
            >
                <Box sx={{ textAlign: 'center' }}>
                    <CircularProgress color="primary" />
                    <Box mt={2} color="white" fontWeight="bold">データを取得中...</Box>
                </Box>
            </Backdrop>
        </Container>
    );
};

export default HRManagerMenu;